
import React, { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { InventoryApiService } from "../../Service/InventoryApiService";
import { Service } from "../../Service/ApiService";
import DataGridLoader from "../Common/DataGridLoader";
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { CommonService } from "../../Service/CommonService";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateRange } from "react-date-range";
import Dropdown from "react-bootstrap/Dropdown";
import { useHistory } from "react-router";
import MultiSelectChipInput from "../Common/MultiSelectChipInput";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Accordion } from "react-bootstrap";

const ServiceRequestBillingReport: React.FC = () => {
    const history = useHistory();
    const previousMonthRange = CommonService.getPreviousMonthDateRange();

    // ── view toggle ──────────────────────────────────────────────────────────
    const [activeView, setActiveView] = useState<"summary" | "detail">("summary");

    // ── table state ──────────────────────────────────────────────────────────
    const [dataList, setDataList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [sortField, setSortField] = useState("companyName");
    const [sortDirection, setSortDirection] = useState("asc");
    const [totalCount, setTotalCount] = useState(0);
    const controllerRef = useRef<AbortController | null>(null);

    // ── filter panel refs ────────────────────────────────────────────────────
    const datePickerRef = useRef<HTMLDivElement | null>(null);
    // const filterRef = useRef<HTMLDivElement | null>(null);
    // const buttonRef = useRef<HTMLButtonElement | null>(null);
    const mainInputRef = useRef<HTMLDivElement | null>(null);

    // ── filter panel state ───────────────────────────────────────────────────
    // const [showPicker, setShowPicker] = useState(false);
    const [mainPickerStyle, setMainPickerStyle] = useState<React.CSSProperties>({});
    const [isMainDatePickerOpen, setIsMainDatePickerOpen] = useState(false);
    // const [filterStyle, setFilterStyle] = useState({});

    // ── filter values ────────────────────────────────────────────────────────
    // startDate / endDate default to previous month (same as CompanyBillingActivityReport)
    const [filter, setFilter] = useState<any>(() => ({
        companyNames: null,
        serviceTypes: null,
        startDate: previousMonthRange.startDateText,
        endDate: previousMonthRange.endDateText,
    }));

    // final committed date range (shown in the input)
    const [selectedDateRange, setSelectedDateRange] = useState<any>({
        startDate: previousMonthRange.startDateText,
        endDate: previousMonthRange.endDateText,
        key: "selection",
    });

    // temp range while picker is open
    const [tempRange, setTempRange] = useState<any>({
        startDate: previousMonthRange.startDateText,
        endDate: previousMonthRange.endDateText,
        key: "selection",
    });

    const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([]);

    useEffect(() => {
        InventoryApiService.getBillableServiceTypes()
            .then((res: any) => setServiceTypeOptions(res.data || []))
            .catch(() => setServiceTypeOptions([]));
    }, []);

    const [dateRangeError, setDateRangeError] = useState<string | null>(null);
    const [allCompanyOptions, setAllCompanyOptions] = useState<string[]>([]);
    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
    const companyOptionsControllerRef = useRef<AbortController | null>(null);

    const isDateRangeValid = (start: string | null, end: string | null): boolean => {
        if (!start || !end) return true;
        const s = new Date(start + "T00:00:00");
        const e = new Date(end + "T00:00:00");
        const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
        return months < 12;
    };

    useEffect(() => {
        if (companyOptionsControllerRef.current) companyOptionsControllerRef.current.abort();
        const controller = new AbortController();
        companyOptionsControllerRef.current = controller;
        Service.fetchCompaniesList()
            .then((res: any) => {
                const names: string[] = (res.data || []).map((c: any) => c.companyName).filter(Boolean).sort();
                setAllCompanyOptions(names);
            })
            .catch(() => {});
    }, []);

    const isFilterApplied = Object.values(filter).some(
        (v) => v !== null && v !== "" && v !== undefined
    );

    const localeText = {
        noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
    };

    // ── helpers ──────────────────────────────────────────────────────────────
    // Display format for UI (MM/DD/YYYY)
    const formatDateDisplay = (date: Date | string | null) => {
        if (!date) return null;
        const d = typeof date === "string" ? new Date(date + "T00:00:00") : date;
        return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
    };

    // Filter format for backend (YYYY-MM-DD)
    const formatDate = (date: Date | string | null) => {
        if (!date) return null;
        const d = typeof date === "string" ? new Date(date + "T00:00:00") : date;
        return d.toLocaleDateString("en-CA"); // en-CA gives YYYY-MM-DD
    };

    const CellWithTooltip = ({ value }: { value: string | number | null | undefined }) => (
        <Tooltip arrow title={value?.toString() ?? ""}>
            <span>{value?.toString() || "-"}</span>
        </Tooltip>
    );

    const renderHeader = (params: any) => (
        <div>
            <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                {params.colDef.headerName}
            </Typography>
        </div>
    );

    // ── data fetch ───────────────────────────────────────────────────────────
    const fetchData = (
        page: number,
        pageSize: number,
        filter: any,
        sortField: string,
        sortDirection: string,
        activeView: string
    ) => {
        const controller = new AbortController();
        const signal = controller.signal;

        // build the payload — companyNames and serviceTypes must be comma-separated strings
        const payload = {
            ...filter,
            companyNames: Array.isArray(filter.companyNames)
                ? filter.companyNames.join(",")
                : filter.companyNames,
            serviceTypes: Array.isArray(filter.serviceTypes)
                ? filter.serviceTypes.join(",")
                : filter.serviceTypes,
        };

        if (!isDateRangeValid(payload.startDate, payload.endDate)) {
            setDateRangeError("Date range cannot exceed 12 months.");
            setIsLoading(false);
            return;
        }
        setDateRangeError(null);

        setIsLoading(true);
        setDataList([]);
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = controller;

        const apiFn =
            activeView === "summary"
                ? InventoryApiService.getServiceRequestBillingSummary
                : InventoryApiService.getServiceRequestBillingDetail;

        apiFn(page, pageSize, payload, sortField, sortDirection, signal)
            .then((response: any) => {
                if (response?.data?.content !== undefined) {
                    setDataList(response.data.content || []);
                    setTotalCount(response.data.totalElements || 0);
                } else if (typeof response?.data === "string") {
                    setDateRangeError(response.data);
                }
                setIsLoading(false);
            })
            .catch((err: any) => {
                if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
                    const msg = err?.response?.data;
                    if (typeof msg === "string" && msg.toLowerCase().includes("date range")) {
                        setDateRangeError(msg);
                    } else {
                        console.error("Error fetching billing data:", err);
                    }
                    setIsLoading(false);
                }
            });
    };

    // initial load
    useEffect(() => {
        fetchData(0, pageSize, filter, sortField, sortDirection, activeView);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView]);

    // ── outside-click: date picker ───────────────────────────────────────────
    // useEffect(() => {
    //     const handler = (e: MouseEvent) => {
    //         const t = e.target as Node;
    //         if (
    //             isMainDatePickerOpen &&
    //             datePickerRef.current &&
    //             !datePickerRef.current.contains(t) &&
    //             mainInputRef.current &&
    //             !mainInputRef.current.contains(t)
    //         ) {
    //             setIsMainDatePickerOpen(false);
    //         }
    //     };
    //     document.addEventListener("mousedown", handler);
    //     return () => document.removeEventListener("mousedown", handler);
    // }, [isMainDatePickerOpen]);

    // ── outside-click: filter panel ──────────────────────────────────────────
    // useEffect(() => {
    //     const handler = (e: MouseEvent) => {
    //         const t = e.target as Node;
    //         if (
    //             filterRef.current &&
    //             !filterRef.current.contains(t) &&
    //             buttonRef.current &&
    //             !buttonRef.current.contains(t)
    //         ) {
    //             setShowPicker(false);
    //             setIsMainDatePickerOpen(false);
    //         }
    //     };
    //     document.addEventListener("mousedown", handler);
    //     return () => document.removeEventListener("mousedown", handler);
    // }, []);

    // sync tempRange when panel opens
    // useEffect(() => {
    //     if (showPicker) {
    //         setTempRange({
    //             startDate: filter.startDate ?? null,
    //             endDate: filter.endDate ?? null,
    //             key: "selection",
    //         });
    //     }
    // }, [showPicker]);

    // reposition filter panel on scroll/resize
    // useEffect(() => {
    //     if (!showPicker) return;
    //     const update = () => {
    //         const h = filterRef.current?.offsetHeight || 500;
    //         setFilterStyle(getFilterPanelPosition(buttonRef.current, 350, h));
    //     };
    //     update();
    //     window.addEventListener("scroll", update, true);
    //     window.addEventListener("resize", update);
    //     return () => {
    //         window.removeEventListener("scroll", update, true);
    //         window.removeEventListener("resize", update);
    //     };
    // }, [showPicker]);

    // useEffect(() => {
    //     if (!showPicker) setIsMainDatePickerOpen(false);
    // }, [showPicker]);

    // reposition date picker on scroll/resize
    useEffect(() => {
        if (!isMainDatePickerOpen) return;
        const update = () =>
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [isMainDatePickerOpen]);

    // ── positioning helpers (identical to CompanyBillingActivityReport) ───────
    // const getFilterPanelPosition = (
    //     el: HTMLElement | null,
    //     panelW = 350,
    //     panelH = 500,
    //     gap = 8
    // ) => {
    //     if (!el) return {};
    //     const r = el.getBoundingClientRect();
    //     const vw = window.innerWidth;
    //     const vh = window.innerHeight;
    //     let left = r.left;
    //     let top = r.bottom + gap;
    //     if (top + panelH > vh) top = r.top - panelH - gap;
    //     left = Math.max(16, Math.min(left, vw - panelW - 16));
    //     top = Math.max(16, Math.min(top, vh - panelH - 16));
    //     return { position: "fixed" as const, top: `${top}px`, left: `${left}px`, zIndex: 9999 };
    // };

    const getFixedPickerPosition = (el: HTMLDivElement | null, pickerW = 700, gap = 8) => {
        if (!el) return {};
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        let left = r.left;
        let top = r.bottom + gap;
        if (left + pickerW > vw - 16) left = vw - pickerW - 16;
        if (left < 16) left = 16;
        return { position: "fixed" as const, top: `${top}px`, left: `${left}px`, zIndex: 9999 };
    };

    // ── filter handlers ──────────────────────────────────────────────────────
    const handleFilterReset = (field: string) => {
        if (field === "companyNames") setSelectedCompanies([]);
        if (field === "serviceTypes") setSelectedServiceTypes([]);
        setFilter((prev: any) => ({ ...prev, [field]: null }));
    };

    // const handleCompanyChange = (tags: string[]) =>
    //     setFilter((prev: any) => ({ ...prev, companyNames: tags.length ? tags : null }));

    // const handleServiceTypeCheck = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
    //     setFilter((prev: any) => {
    //         const current: string[] = Array.isArray(prev.serviceTypes)
    //             ? prev.serviceTypes
    //             : prev.serviceTypes
    //             ? prev.serviceTypes.split(",")
    //             : [];
    //         const next = e.target.checked
    //             ? [...current, type]
    //             : current.filter((t) => t !== type);
    //         return { ...prev, serviceTypes: next.length ? next : null };
    //     });
    // };

    const handleMainClear = () => {
        const emptyRange = { startDate: null, endDate: null, key: "selection" };
        setTempRange(emptyRange);
        setSelectedDateRange(emptyRange);
        setIsMainDatePickerOpen(false);
        setFilter((prev: any) => ({ ...prev, startDate: null, endDate: null }));
    };

    const handleMainPickerToggle = () => {
        if (!isMainDatePickerOpen)
            setMainPickerStyle(getFixedPickerPosition(mainInputRef.current, 700));
        setIsMainDatePickerOpen((p) => !p);
    };

    const handleTempChange = (start: Date | null, end: Date | null) =>
        setTempRange({ startDate: start, endDate: end, key: "selection" });

    const handleMainCancel = () => {
        setTempRange(selectedDateRange);
        setIsMainDatePickerOpen(false);
    };

    const handleMainOk = () => {
        setSelectedDateRange(tempRange);
        setIsMainDatePickerOpen(false);
        setFilter((prev: any) => ({
            ...prev,
            startDate: formatDate(tempRange.startDate),
            endDate: formatDate(tempRange.endDate),
        }));
    };

    const handleClear = () => {
        const cleared = {
            companyNames: null,
            serviceTypes: null,
            startDate: null,
            endDate: null,
        };
        setFilter(cleared);
        setSelectedCompanies([]);
        setSelectedServiceTypes([]);   // ← add this line
        setDateRangeError(null);
        setSelectedDateRange({ startDate: null, endDate: null, key: "selection" });
        setTempRange({ startDate: null, endDate: null, key: "selection" });
        setPage(0);
        fetchData(0, pageSize, cleared, sortField, sortDirection, activeView);
    };

    const handleApply = () => {
        const merged = {
            ...filter,
            companyNames: selectedCompanies.length > 0 ? selectedCompanies.join(",") : null,
            serviceTypes: selectedServiceTypes.length > 0 ? selectedServiceTypes.join(",") : null,
        };
        setFilter(merged);
        setPage(0);
        fetchData(0, pageSize, merged, sortField, sortDirection, activeView);
    };

    const handleSortChange = (sortModel: any) => {
        if (sortModel.length > 0) {
            const sf = sortModel[0].field;
            const sd = sortModel[0].sort;
            setSortField(sf);
            setSortDirection(sd);
            fetchData(page, pageSize, filter, sf, sd, activeView);
        }
    };

    const onPaginationModelChange = (m: any) => {
        setPage(m.page);
        setPageSize(m.pageSize);
        fetchData(m.page, m.pageSize, filter, sortField, sortDirection, activeView);
    };

    const exportData = () => {
        const controller = new AbortController();
        const signal = controller.signal;
        const payload = {
            ...filter,
            companyNames: Array.isArray(filter.companyNames)
                ? filter.companyNames.join(",")
                : filter.companyNames,
            serviceTypes: Array.isArray(filter.serviceTypes)
                ? filter.serviceTypes.join(",")
                : filter.serviceTypes,
        };
        const exportFn =
            activeView === "summary"
                ? InventoryApiService.exportServiceRequestBillingSummary
                : InventoryApiService.exportServiceRequestBillingDetail;

        exportFn(payload, sortField, sortDirection, signal);
        CommonService.Toast.fire({
            title: "Your export is in progress. We'll notify you when it's ready!",
            icon: "success",
        });
    };

    // ── view switch ──────────────────────────────────────────────────────────
    const switchView = (v: "summary" | "detail") => {
        if (v === activeView) return;
        setActiveView(v);
        setPage(0);
        // fetchData is triggered by the useEffect([activeView])
    };

    // ── company-wise total rows (computed client-side) ───────────────────────
    // After data loads, inject a "Total for <Company>" row after each company's last row
    const buildRowsWithTotals = (rows: any[]): any[] => {
        if (!rows.length) return rows;
        const result: any[] = [];
        let i = 0;
        while (i < rows.length) {
            const company = rows[i].companyName;
            const companyRows: any[] = [];
            while (i < rows.length && rows[i].companyName === company) {
                companyRows.push(rows[i]);
                i++;
            }
            result.push(...companyRows);
            // Build per-service-type totals for this company
            const byType: Record<string, any> = {};
            companyRows.forEach((r) => {
                const key = r.serviceType;
                if (!byType[key]) {
                    byType[key] = {
                        id: `total-${company}-${key}`,
                        companyName: `Total for ${company}`,
                        serviceType: key,
                        unitType: r.unitType,
                        timeSpentOnRequest: 0,
                        successCount: 0,
                        barrelCount: 0,
                        drumCount: 0,
                        toteCount: 0,
                        requestDate: null,
                        requestNumber: null,
                        isTotalRow: true,
                    };
                }
                byType[key].timeSpentOnRequest += r.timeSpentOnRequest ?? 0;
                byType[key].successCount += r.successCount ?? 0;
                byType[key].barrelCount += r.barrelCount ?? 0;
                byType[key].drumCount += r.drumCount ?? 0;
                byType[key].toteCount += r.toteCount ?? 0;
            });
            Object.values(byType).forEach((t) => result.push(t));
        }
        return result;
    };

    // Total rows are shown in Excel export only — DataGrid shows raw data
    const displayRows = dataList;

    // ── columns ──────────────────────────────────────────────────────────────
    const qtyCell = (params: any) => {
        const row = params.row;
        const barrelCount = row.barrelCount ?? 0;
        const drumCount = row.drumCount ?? 0;
        const toteCount = row.toteCount ?? 0;
        const containerTotal = barrelCount + drumCount + toteCount;
        // Use container breakdown total if available, otherwise fall back to successCount
        const displayTotal = containerTotal > 0 ? containerTotal : (row.successCount ?? 0);

        return (
            <div className="d-flex align-items-center">
                <CellWithTooltip value={displayTotal > 0 ? displayTotal : "-"} />
                <Dropdown drop="end" className="d-inline-block report-dropdown">
                    <Dropdown.Toggle
                        variant="basic"
                        id={`qty-info-${row.id}`}
                        className="p-0 ps-1 border-0 outline-0"
                        style={{ marginTop: "-5px" }}
                    >
                        <InfoIcon sx={{ color: "#6F6F6F", fontSize: 18 }} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item>
                            <p className="key">Barrel</p>
                            <p className="value">{barrelCount}</p>
                        </Dropdown.Item>
                        <Dropdown.Item>
                            <p className="key">Tote</p>
                            <p className="value">{toteCount}</p>
                        </Dropdown.Item>
                        <Dropdown.Item>
                            <p className="key">Drum</p>
                            <p className="value">{drumCount}</p>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const unitsCell = (params: any) => {
        const row = params.row;
        if (row.unitType === "Hours") {
            return <CellWithTooltip value={row.timeSpentOnRequest != null ? `${row.timeSpentOnRequest} hrs` : "-"} />;
        }
        return qtyCell(params);
    };

    const summaryColumns: GridColDef[] = [
        {
            field: "companyName",
            headerName: "Company Name",
            flex: 1,
            minWidth: 200,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.companyName} />,
        },
        {
            field: "serviceType",
            headerName: "Request Type",
            flex: 1,
            minWidth: 200,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.serviceType} />,
        },
        {
            field: "unitType",
            headerName: "Unit Type",
            flex: 1,
            minWidth: 130,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.unitType} />,
        },
        {
            field: "units",
            headerName: "Total Units",
            flex: 1,
            minWidth: 220,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",
            cellClassName: "status-badge",
            renderHeader,
            renderCell: unitsCell,
        },
    ];

    const detailColumns: GridColDef[] = [
        {
            field: "companyName",
            headerName: "Company Name",
            flex: 1,
            minWidth: 200,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.companyName} />,
        },
        {
            field: "requestDate",
            headerName: "Request Date",
            flex: 1,
            minWidth: 150,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => (
                <CellWithTooltip
                    value={p.row.isTotalRow ? "" : (p.row.requestDate ? CommonService.formatDateForSupportRequest(p.row.requestDate) : "-")}
                />
            ),
        },
        {
            field: "requestNumber",
            headerName: "Request #",
            flex: 1,
            minWidth: 160,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => {
                if (p.row.isTotalRow || !p.row.requestNumber) {
                    return <CellWithTooltip value={p.row.isTotalRow ? "" : "-"} />;
                }
                const linkPrefix = CommonService.isAdmin() ? "/master" : "/user";
                return (
                    <div className="d-flex align-items-center h-100">
                        <Tooltip arrow title={p.row.requestNumber}>
                            <span
                                style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline" }}
                                onClick={() => {
                                    if (p.row.id) {
                                        history.push(`${linkPrefix}/view-customer-service-request/${p.row.id}`);
                                    }
                                }}
                            >
                                {p.row.requestNumber}
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            field: "serviceType",
            headerName: "Request Type",
            flex: 1,
            minWidth: 200,
            sortable: true,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.serviceType} />,
        },
        {
            field: "unitType",
            headerName: "Unit Type",
            flex: 1,
            minWidth: 130,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",
            renderHeader,
            renderCell: (p) => <CellWithTooltip value={p.row.unitType} />,
        },
        {
            field: "units",
            headerName: "Units",
            flex: 1,
            minWidth: 220,
            sortable: false,
            headerClassName: "table-header border-0 my-2 h-100",
            cellClassName: "status-badge",
            renderHeader,
            renderCell: unitsCell,
        },
    ];

    const getRowId = (row: any) => row.id ?? `${row.companyName}-${row.serviceType}-${row.requestDate}`;

    // const selectedServiceTypes: string[] = Array.isArray(filter.serviceTypes)
    //     ? filter.serviceTypes
    //     : filter.serviceTypes
    //     ? filter.serviceTypes.split(",")
    //     : [];
    const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);

    // ── render ───────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── header row: export btn (left) | tabs (right) ── */}
            <div className="reporting-filter-btn-wrapper service-billing-rpt">
                <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">

                    {/* LEFT: export */}
                    <div className="position-relative">
                        {(dataList.length > 0 || isLoading) && (
                            <button
                                title="Export to Excel"
                                className="primary-btn d-inline-flex align-items-center py-1 export-btn"
                                style={{ paddingTop: "1px", paddingLeft: 11, paddingBottom: "3px" }}
                                onClick={(e) => { e.currentTarget.blur(); exportData(); }}
                            >
                                <FileDownloadOutlinedIcon sx={{ marginTop: "5.5px" }} />
                                Export
                            </button>
                        )}
                    </div>

                    {/* RIGHT: tab buttons — identical pattern to MyInventories */}
                    <div className="service-req-tabs">
                        <div
                            className="btn-group user-activity-btn-grp flex-wrap"
                            role="group"
                            aria-label="View toggle"
                        >
                            <button
                                className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                                    activeView === "summary" ? "bg-theme" : "bg-theme-white"
                                }`}
                                onClick={() => switchView("summary")}
                            >
                                Summary
                            </button>
                            <button
                                className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                                    activeView === "detail" ? "bg-theme" : "bg-theme-white"
                                }`}
                                onClick={() => switchView("detail")}
                            >
                                Detail
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── data grid ─────────────────────────────────────────────────── */}
            {dateRangeError && (
                <div className="alert barrel-alert alert-dismissible fade show mx-3 mt-2 mb-0" role="alert">
                    <span className="d-flex align-items-center gap-2"><WarningIcon />{dateRangeError}</span>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setDateRangeError(null)}><CloseIcon /></button>
                </div>
            )}
            <div className="user-table-container report-module">
                    <Accordion defaultActiveKey="0" onSelect={() => setIsMainDatePickerOpen(false)} className="filter-accordion">
                        <Accordion.Item eventKey="0" className="filter-accordion-item">
                            <Accordion.Header className="filter-accordion-header">
                                <span className="accordion-filter-title">Filters</span>
                                <button
                                    className="filter-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClear();
                                    }}
                                >
                                    Reset All
                                </button>
                            </Accordion.Header>
                            <Accordion.Body className="filter-accordion-body p-0">
                                <div className="filter-panel-accordion">

                                    <div className="filter-body">

                                        {/* Date range */}
                                        <div className="filter-section position-relative">
                                            <div className="section-header">
                                                <h4>Date range</h4>
                                                <button className="section-btn" type="button" onClick={handleMainClear}>Reset</button>
                                            </div>
                                            {/* <label htmlFor="dateRange">Filter by Date Range</label> */}
                                            <div className="date-input-wrapper" ref={mainInputRef}>
                                                <input
                                                    type="text"
                                                    placeholder="Select date range"
                                                    readOnly
                                                    className="form-control"
                                                    value={
                                                        selectedDateRange.startDate && selectedDateRange.endDate
                                                            ? `${formatDateDisplay(selectedDateRange.startDate)} - ${formatDateDisplay(selectedDateRange.endDate)}`
                                                            : ""
                                                    }
                                                    onClick={handleMainPickerToggle}
                                                />
                                                <svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M13.5 1.51172H12.7266V0H11.25V1.51172H3.72656V0H2.25V1.51172H1.47656C0.667969 1.51172 0 2.17969 0 3.02344V15.0117C0 15.8203 0.667969 16.5234 1.47656 16.5234H13.5C14.3086 16.5234 14.9766 15.8203 14.9766 15.0117V3.02344C14.9766 2.17969 14.3086 1.51172 13.5 1.51172ZM13.5 15.0117H1.47656V6.75H13.5V15.0117ZM13.5 5.27344H1.47656V3.02344H13.5V5.27344Z" fill="#9CA3AF" />
                                                </svg>
                                            </div>

                                            {isMainDatePickerOpen && (
                                                <div className="datepicker-menu" ref={datePickerRef} style={mainPickerStyle}>
                                                    <DateRange
                                                        ranges={[tempRange]}
                                                        editableDateInputs={true}
                                                        dropdownMode="scroll"
                                                        onChange={(item: any) =>
                                                            handleTempChange(
                                                                item.selection.startDate ?? null,
                                                                item.selection.endDate ?? null
                                                            )
                                                        }
                                                        moveRangeOnFirstSelection={false}
                                                        months={2}
                                                        direction="horizontal"
                                                        showDateDisplay={false}
                                                    />
                                                    <div className="datepicker-menu-bottom">
                                                        <button type="button" onClick={handleMainCancel} className="datepicker-btn">
                                                            CANCEL
                                                        </button>
                                                        <button type="button" onClick={handleMainOk} className="datepicker-btn">
                                                            OK
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Company */}
                                        <div className="filter-section">
                                            <div className="section-header">
                                                <h4>Company</h4>
                                                <button className="section-btn" type="button" onClick={() => handleFilterReset("companyNames")}>Reset</button>
                                            </div>
                                            <MultiSelectChipInput
                                                selected={selectedCompanies}
                                                options={allCompanyOptions}
                                                placeholder="Select companies"
                                                searchPlaceholder="Search company"
                                                onToggle={val => setSelectedCompanies(prev =>
                                                    prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                )}
                                                onSelectAll={() => setSelectedCompanies(prev =>
                                                    prev.length === allCompanyOptions.length ? [] : [...allCompanyOptions]
                                                )}
                                                onReset={() => setSelectedCompanies([])}
                                            />
                                        </div>

                                        {/* Service Request Type */}
                                        <div className="filter-section">
                                            <div className="section-header">
                                                <h4>Service Request Type</h4>
                                                <button className="section-btn" type="button" onClick={() => handleFilterReset("serviceTypes")}>Reset</button>
                                            </div>
                                            <MultiSelectChipInput
                                                selected={selectedServiceTypes}
                                                options={serviceTypeOptions}
                                                placeholder="Select service types"
                                                searchPlaceholder="Search service type"
                                                onToggle={val => setSelectedServiceTypes(prev =>
                                                    prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
                                                )}
                                                onSelectAll={() => setSelectedServiceTypes(prev =>
                                                    prev.length === serviceTypeOptions.length ? [] : [...serviceTypeOptions]
                                                )}
                                                onReset={() => setSelectedServiceTypes([])}
                                            />
                                        </div>

                                    </div>

                                    <div className="filter-bottom">
                                        <div className="filter-actions">
                                            <button className="filter-btn" onClick={handleApply}>
                                                <CheckIcon /> Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                <div className="userTable">
                    <DataGrid
                        rows={displayRows}
                        columns={activeView === "summary" ? summaryColumns : detailColumns}
                        className="data-grid"
                        pagination
                        paginationModel={{ page, pageSize }}
                        onPaginationModelChange={onPaginationModelChange}
                        rowCount={totalCount}
                        paginationMode="server"
                        sortingMode="server"
                        onSortModelChange={handleSortChange}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 20, 50, 100]}
                        localeText={localeText}
                        getRowId={getRowId}
                        loading={isLoading}
                        slots={{ loadingOverlay: DataGridLoader }}
                        disableColumnResize
                        sx={{
                            "& .MuiDataGrid-columnHeaderCheckbox": {
                                borderBottom: "unset !important",
                            },
                        }}                    />
                </div>
            </div>
        </>
    );
};

export default ServiceRequestBillingReport;
